/**
 * Import a CSV for ANY entity kind - preview first, then apply.
 *
 * The round trip this belongs to is: export the kind, edit it in a spreadsheet,
 * bring it back. So the export button sits next to the import button on the
 * screen, and this dialog never writes anything on the strength of a filename:
 * choosing a file sends `dry_run: true`, which is read-only, and what comes
 * back is what WOULD happen, per row. Applying is a second, explicit request
 * with `dry_run: false`.
 *
 * That ordering is the whole point. A bulk edit of two hundred elements is not
 * something to find out about afterwards, and `unchanged` is reported rather
 * than hidden because "sixty rows, four changed" is the answer somebody is
 * actually looking for.
 *
 * It was requirements-only until issue #33, and nothing about it needed to be:
 * every sentence it said about a requirement it now says about `kind.label`,
 * which is the move the back end made by reading its columns off the registry.
 * The only kind-specific thing left is which route it posts to.
 *
 * The route may not be deployed yet, so a 404 is stated in a sentence naming
 * the endpoint rather than shown as a failure or left as a spinner.
 */

import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { DataGrid } from '@mui/x-data-grid'
import type { GridColDef } from '@mui/x-data-grid'
import { ApiError, api } from '../../api'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import { naturalComparator } from '../common/naturalCompare'
import { decodeCsv } from './csvText'
import type { CsvImportResult, CsvRowOutcome, KindSchema } from '../../schema'

const ACTIONS: CsvRowOutcome['action'][] = ['created', 'updated', 'unchanged', 'failed']

/** MUI's colour for each outcome. 'failed' is the only one that is a problem. */
const ACTION_COLOUR: Record<CsvRowOutcome['action'], 'success' | 'info' | 'default' | 'error'> = {
  created: 'success',
  updated: 'info',
  unchanged: 'default',
  failed: 'error',
}

/**
 * The headline counts, however the route spells them.
 *
 * The contract's shape nests them under `counts`, which is what this reads
 * first - computing them here as well is how the headline and the rows come to
 * disagree. The two fallbacks exist because the service's own `as_dict()` puts
 * the same four numbers at the top level, and because a report with rows and no
 * counts at all should still show a headline rather than four zeroes.
 */
function countsOf(result: CsvImportResult): Record<CsvRowOutcome['action'], number> {
  const flat = result as unknown as Record<string, unknown>
  if (result.counts) return result.counts
  if (ACTIONS.some((action) => typeof flat[action] === 'number')) {
    return Object.fromEntries(
      ACTIONS.map((action) => [action, Number(flat[action] ?? 0)]),
    ) as Record<CsvRowOutcome['action'], number>
  }
  const tally = { created: 0, updated: 0, unchanged: 0, failed: 0 }
  for (const row of result.rows ?? []) tally[row.action] += 1
  return tally
}

/** How many rows applying would actually write. */
function changeCount(result: CsvImportResult): number {
  const counts = countsOf(result)
  return counts.created + counts.updated
}

const ROW_COLUMNS: GridColDef[] = [
  { field: 'line', headerName: 'Line', width: 70, type: 'number', align: 'left', headerAlign: 'left' },
  {
    field: 'identifier',
    headerName: 'ID',
    minWidth: 140,
    sortComparator: naturalComparator,
    renderCell: (params) => (
      <Box sx={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}>{String(params.value ?? '')}</Box>
    ),
  },
  {
    field: 'action',
    headerName: 'Would',
    minWidth: 120,
    renderCell: (params) => {
      const action = params.value as CsvRowOutcome['action']
      return <Chip size="small" label={action} color={ACTION_COLOUR[action] ?? 'default'} />
    },
  },
  {
    field: 'changed',
    headerName: 'Fields',
    flex: 1,
    minWidth: 160,
    valueGetter: (value: unknown) => (Array.isArray(value) ? value.join(', ') : (value ?? '')),
  },
  { field: 'detail', headerName: 'Detail', flex: 2, minWidth: 200 },
]

export interface ImportCsvDialogProps {
  /** The kind being imported, which is where every noun on this dialog comes from. */
  kind: KindSchema
  onClose: () => void
  /** Called after a real apply, so the listing behind can re-read itself. */
  onImported: () => void
}

export default function ImportCsvDialog({ kind, onClose, onImported }: ImportCsvDialogProps) {
  const toasts = useToasts()

  /** The endpoint this dialog is nothing without, named for the 404 sentence. */
  const route = `POST /api/${kind.plural}/import`
  const noun = kind.label.toLowerCase()

  const [filename, setFilename] = useState('')
  const [csv, setCsv] = useState('')
  /** Bytes read as Windows-1252 because the file was not all UTF-8 (issue #87). */
  const [legacyBytes, setLegacyBytes] = useState(0)
  const [preview, setPreview] = useState<CsvImportResult | null>(null)
  const [applied, setApplied] = useState<CsvImportResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')
  /** Set when the route itself is not there, which reads differently. */
  const [notDeployed, setNotDeployed] = useState(false)

  /** Turn any refusal into something on the page, never into a thrown render. */
  const explain = (error: unknown) => {
    const failed = error as ApiError
    if (failed?.status === 404) {
      setNotDeployed(true)
      setFailure('')
      return
    }
    setFailure(failed?.message ?? 'The import could not be run.')
  }

  const choose = async (file: File | undefined) => {
    if (!file) return
    setFilename(file.name)
    setPreview(null)
    setApplied(null)
    setFailure('')
    setNotDeployed(false)
    setLegacyBytes(0)
    setBusy(true)
    try {
      // Read here rather than sending the File: the API takes the text, so the
      // browser does the decoding and there is no multipart route to keep.
      // NOT file.text(), which is UTF-8 only and turned every bullet Excel
      // saved into a U+FFFD diamond in the model - see csvText.
      const { text, legacyBytes: legacy } = decodeCsv(await file.arrayBuffer())
      setCsv(text)
      setLegacyBytes(legacy)
      // dry_run: true is READ-ONLY. This is the only request a chosen file
      // makes, so picking the wrong file changes nothing.
      const report = await api.importEntities(kind.plural, { csv: text, dry_run: true })
      setPreview(report)
    } catch (error) {
      explain(error)
    } finally {
      setBusy(false)
    }
  }

  const apply = async () => {
    // Only ever from a preview: there is no path to a write that has not been
    // shown to somebody first.
    if (!preview || !csv) return
    setBusy(true)
    setFailure('')
    try {
      const report = await api.importEntities(kind.plural, { csv, dry_run: false })
      setApplied(report)
      const counts = countsOf(report)
      toasts.succeeded(
        `${counts.created} created, ${counts.updated} updated in one commit`,
      )
      onImported()
    } catch (error) {
      explain(error)
    } finally {
      setBusy(false)
    }
  }

  const result = applied ?? preview
  const counts = result ? countsOf(result) : null
  const changes = preview ? changeCount(preview) : 0

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import {noun}s from CSV</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {notDeployed ? (
            <Alert severity="info">
              Importing needs {route}, which is not deployed yet. Exporting works now; the import
              half of the round trip arrives with that route.
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Export the {noun}s, edit them in a spreadsheet, and bring the file back here. A row
              whose ID already exists is updated and a new ID is created. Nothing is written until
              you apply, and applying writes the whole file in one commit rather than one commit
              per row.
            </Typography>
          )}

          {failure && <Alert severity="error">{failure}</Alert>}

          {/* A guess about the person's file, so it is said rather than made
              silently. Excel's plain CSV format is the usual cause. */}
          {legacyBytes > 0 && (
            <Alert severity="info">
              This file is not saved as UTF-8, so {legacyBytes}{' '}
              {legacyBytes === 1 ? 'character was' : 'characters were'} read as Windows-1252, the
              encoding Excel uses for plain CSV. Check bullets, dots and degree signs in the rows
              it changes; saving as &quot;CSV UTF-8&quot; avoids the guess.
            </Alert>
          )}

          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
              Choose a CSV
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(event) => void choose(event.target.files?.[0])}
              />
            </Button>
            <Typography variant="body2" sx={{ fontFamily: MONOSPACE }}>
              {filename || 'No file chosen'}
            </Typography>
            {busy && <CircularProgress size={18} />}
          </Stack>

          {result && counts && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {applied ? 'Applied' : 'Preview — nothing has been written yet'}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  {ACTIONS.map((action) => (
                    <Chip
                      key={action}
                      size="small"
                      color={counts[action] ? ACTION_COLOUR[action] : 'default'}
                      variant={counts[action] ? 'filled' : 'outlined'}
                      label={`${counts[action]} ${action}`}
                    />
                  ))}
                </Stack>
              </Box>

              {/* Both column lists, in plain words. A spreadsheet round trip
                  reorders and drops columns, none of which is an error - but
                  silently ignoring a column somebody spent an afternoon filling
                  in would be. */}
              {result.ignored_columns?.length > 0 && (
                <Alert severity="warning">
                  {result.ignored_columns.length === 1 ? 'This column is' : 'These columns are'} not
                  a {noun} field, so {result.ignored_columns.length === 1 ? 'it' : 'they'} will be
                  ignored: {result.ignored_columns.join(', ')}.
                </Alert>
              )}
              {result.missing_columns?.length > 0 && (
                <Alert severity="info">
                  The file has no column for {result.missing_columns.join(', ')}, so{' '}
                  {result.missing_columns.length === 1 ? 'that field' : 'those fields'} will be left
                  exactly as {result.missing_columns.length === 1 ? 'it is' : 'they are'}.
                </Alert>
              )}
              {counts.failed > 0 && (
                <Alert severity="error">
                  {counts.failed} {counts.failed === 1 ? 'row' : 'rows'} cannot be applied and will
                  be skipped. The reason for each is in the Detail column.
                </Alert>
              )}

              {result.rows?.length > 0 && (
                <Box sx={{ height: 340 }}>
                  <DataGrid
                    rows={result.rows.map((row) => ({ ...row, id: row.line }))}
                    columns={ROW_COLUMNS}
                    density="compact"
                    disableRowSelectionOnClick
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    pageSizeOptions={[25, 50, 100]}
                    sx={{ border: 0 }}
                  />
                </Box>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {applied ? 'Close' : 'Cancel'}
        </Button>
        {!applied && (
          <Button
            variant="contained"
            onClick={() => void apply()}
            // No preview, or a preview that would change nothing: there is
            // nothing to apply, and offering it invites the click that people
            // then wonder about.
            disabled={busy || !preview || changes === 0}
          >
            {changes > 0 ? `Apply ${changes} ${changes === 1 ? 'change' : 'changes'}` : 'Apply'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
